import { useCallback, useEffect, useRef, useState } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import jwtInstance from '../guards/jwt.interceptor';
import { API_NOTIFICATIONS } from '../utils/restApi';
import showNotification from '../components/extras/showNotification';

export interface AppNotification {
    id: number;
    message: string;
    type: string; // 'SUCCESS' | 'ERROR' | 'WARNING' | 'INFO'
    relatedEntityUuid?: string | null;
    read: boolean;
    createdAt: string;
}

/**
 * Name of the window-level CustomEvent fired for every incoming WS notification.
 * Components that need to react to a specific entity's lifecycle (e.g. a match's
 * disciplinary processing state) subscribe here and filter by relatedEntityUuid.
 * The detail payload is the {@link AppNotification}.
 */
export const APP_NOTIFICATION_EVENT = 'app:notification';

interface UseNotificationsReturn {
    notifications: AppNotification[];
    unreadCount: number;
    isDrawerOpen: boolean;
    loading: boolean;
    openDrawer: () => void;
    closeDrawer: () => void;
    fetchNotifications: () => Promise<void>;
}

const useNotifications = (): UseNotificationsReturn => {
    const [notifications, setNotifications] = useState<AppNotification[]>([]);
    const [unreadCount, setUnreadCount] = useState(0);
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const stompClientRef = useRef<Client | null>(null);

    const fetchUnreadCount = useCallback(() => {
        jwtInstance
            .get(API_NOTIFICATIONS.unreadCount)
            .then((res) => {
                if (res.status === 200) {
                    setUnreadCount(res.data.count ?? 0);
                }
            })
            .catch(() => {
                // Silently ignore — e.g., non-manager users may get 403
            });
    }, []);

    const fetchNotifications = useCallback(() => {
        setLoading(true);
        return jwtInstance
            .get(API_NOTIFICATIONS.all, { params: { page: 0, size: 50 } })
            .then((res) => {
                if (res.status === 200) {
                    setNotifications(res.data.content ?? []);
                }
            })
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    const markAllAsRead = useCallback(() => {
        jwtInstance
            .put(API_NOTIFICATIONS.markRead)
            // the local `read` flags stay as fetched while the drawer is open, so the
            // drawer can still highlight what was new; they are cleared on close
            .then(() => setUnreadCount(0))
            .catch(() => {});
    }, []);

    const openDrawer = useCallback(() => {
        setIsDrawerOpen(true);
        // Mark all as read when opening the drawer, after the list is fetched with its unread flags
        fetchNotifications().then(markAllAsRead);
    }, [fetchNotifications, markAllAsRead]);

    const closeDrawer = useCallback(() => {
        setIsDrawerOpen(false);
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
    }, []);

    // Fetch initial unread count on mount
    useEffect(() => {
        fetchUnreadCount();
    }, [fetchUnreadCount]);

    // Set up WebSocket subscription for real-time notifications
    useEffect(() => {
        const token = JSON.parse(localStorage.getItem('user') ?? '{}')?.token;
        if (!token) return;

        const endpoint = process.env.REACT_APP_ENDPOINT ?? '';

        const client = new Client({
            // Use SockJS as the transport factory (matches backend /ws endpoint with SockJS)
            webSocketFactory: () => new SockJS(`${endpoint}/ws`) as WebSocket,
            connectHeaders: {
                Authorization: `Bearer ${token}`,
            },
            reconnectDelay: 5000,
            onConnect: () => {
                // Subscribe to the user-specific notification queue.
                // The STOMP server routes to this destination using the authenticated principal
                // (email), matching WebSocketConfig's userDestinationPrefix="/user".
                client.subscribe('/user/queue/notifications', (frame) => {
                    try {
                        const incoming: AppNotification = JSON.parse(frame.body);
                        setNotifications((prev) => [incoming, ...prev]);
                        setUnreadCount((prev) => prev + 1);

                        // Show a toast so the manager sees the result even if the drawer is closed
                        const toastType =
                            incoming.type === 'SUCCESS'
                                ? 'success'
                                : incoming.type === 'ERROR'
                                ? 'danger'
                                : 'info';
                        showNotification('Notificare nouă', incoming.message, toastType);

                        // Fan out to any component that wants to react to this specific entity
                        // (e.g. clear a per-match "processing" badge when disciplinary finishes).
                        window.dispatchEvent(
                            new CustomEvent<AppNotification>(APP_NOTIFICATION_EVENT, {
                                detail: incoming,
                            }),
                        );
                    } catch (e) {
                        // Ignore malformed frames
                    }
                });
            },
            onStompError: () => {
                // Connection errors are silently swallowed; reconnect happens automatically
            },
        });

        client.activate();
        stompClientRef.current = client;

        return () => {
            client.deactivate();
            stompClientRef.current = null;
        };
    }, []); // run once on mount — token is stable for the lifetime of the session

    return {
        notifications,
        unreadCount,
        isDrawerOpen,
        loading,
        openDrawer,
        closeDrawer,
        fetchNotifications,
    };
};

export default useNotifications;
